/**
 * Stock Adjustment Dialog Component
 *
 * Dialog for adjusting stock quantities with reasons and notes.
 * Creates inventory transactions for audit trails.
 */

import { useState, useEffect } from 'react';
import {
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Paper,
  LinearProgress,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { parseEuroInput } from '@/features/inventory/costing/costMoney';
import type { StockLevelItem, WarehouseItem } from '@/hooks/useInventory';
import type { InventoryItem } from '@/types';
import { useTranslation } from 'react-i18next';
import { NumberField } from '@/components/common/NumberField';

type AdjustmentType =
  | 'purchase'
  | 'sale'
  | 'adjustment'
  | 'return'
  | 'damage'
  | 'count'
  | 'transfer';

interface StockAdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  warehouses: WarehouseItem[];
  levels: StockLevelItem[];
  loading: boolean;
  error: boolean;
  saving: boolean;
  onRetry: () => void;
  onAdjust: (adjustment: {
    warehouseId: string;
    binLocationId: string | null;
    type: AdjustmentType;
    quantity: number;
    reason: string;
    notes?: string;
    /** YYYY-MM-DD; explicit null clears the expiry on the row (#209) */
    expiryDate?: string | null;
    /**
     * Cost of the incoming units, cents with four decimals as text (`"100.1250"`, `"0.0000"` is a real
     * zero). Present only when the user typed one for an increase; absent means unknown, never the
     * catalog cost. Never sent for a decrease or a return that names a work order.
     */
    unitCostCents?: string;
    /** Work order id of a return of used parts: the server restores those units at their original cost. */
    referenceId?: string;
  }) => Promise<void>;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function StockAdjustmentDialog({
  open,
  onClose,
  item,
  onAdjust,
  warehouses,
  levels,
  loading,
  error,
  saving,
  onRetry,
}: StockAdjustmentDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tCost } = useTranslation('inventoryCosting');
  const { isDirty, markDirty } = useFormDirty(open);

  const [adjustmentMode, setAdjustmentMode] = useState<'increase' | 'decrease'>('increase');
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('adjustment');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [binLocationId, setBinLocationId] = useState('');
  const [submitError, setSubmitError] = useState(false);
  const { t: tc } = useTranslation('common');
  const defaultWarehouseId = warehouses.find((w) => w.isDefault)?.id ?? warehouses[0]?.id ?? '';
  const warehouse = warehouses.find((w) => w.id === warehouseId);
  const level = levels.find(
    (l) => l.warehouseId === warehouseId && (l.binLocationId ?? '') === binLocationId
  );
  const currentQuantity = level?.quantityOnHand ?? 0;
  const reservedQuantity = level?.quantityReserved ?? 0;

  const adjustmentTypes: { value: AdjustmentType; label: string; description: string }[] = [
    {
      value: 'purchase',
      label: t('stockAdjustment.adjustmentTypes.purchase'),
      description: t('stockAdjustment.adjustmentTypes.purchaseDesc'),
    },
    {
      value: 'sale',
      label: t('stockAdjustment.adjustmentTypes.sale'),
      description: t('stockAdjustment.adjustmentTypes.saleDesc'),
    },
    {
      value: 'adjustment',
      label: t('stockAdjustment.adjustmentTypes.adjustment'),
      description: t('stockAdjustment.adjustmentTypes.adjustmentDesc'),
    },
    {
      value: 'return',
      label: t('stockAdjustment.adjustmentTypes.return'),
      description: t('stockAdjustment.adjustmentTypes.returnDesc'),
    },
    {
      value: 'damage',
      label: t('stockAdjustment.adjustmentTypes.damage'),
      description: t('stockAdjustment.adjustmentTypes.damageDesc'),
    },
    {
      value: 'count',
      label: t('stockAdjustment.adjustmentTypes.count'),
      description: t('stockAdjustment.adjustmentTypes.countDesc'),
    },
    {
      value: 'transfer',
      label: t('stockAdjustment.adjustmentTypes.transfer'),
      description: t('stockAdjustment.adjustmentTypes.transferDesc'),
    },
  ];

  useEffect(() => {
    // Key reset on item.id so a new object for the same part does not wipe in-progress edits.
    if (open && item?.id) {
      setAdjustmentMode('increase');
      setAdjustmentType('adjustment');
      setQuantity(1);
      setNotes('');
      setUnitCost('');
      setReferenceId('');
      setWarehouseId(defaultWarehouseId);
      setBinLocationId('');
      setSubmitError(false);
    }
  }, [open, item?.id, defaultWarehouseId]);

  useEffect(() => {
    setExpiryDate(level?.expiryDate ?? '');
  }, [level?.expiryDate, warehouseId, binLocationId]);

  if (!item) return null;

  const newQuantity =
    adjustmentMode === 'increase' ? currentQuantity + quantity : currentQuantity - quantity;
  const validTarget =
    !!warehouse && (!binLocationId || warehouse.binLocations.some((b) => b.id === binLocationId));
  const invalidQuantity =
    !Number.isInteger(quantity) || quantity < 1 || newQuantity < reservedQuantity;
  const totalAfter = item.quantity + newQuantity - currentQuantity;

  // What the incoming units cost. Only an increase has a cost; a return that names a work order restores
  // units at the cost the ledger recorded for them, so a cost cannot be entered there either. Empty is
  // unknown (never the catalog cost) and a typed 0 is a real, free-of-charge cost.
  const isIncrease = adjustmentMode === 'increase';
  const isReturn = adjustmentType === 'return';
  const trimmedReference = referenceId.trim();
  const showReference = isReturn && isIncrease;
  const referenceInvalid = showReference && trimmedReference !== '' && !UUID.test(trimmedReference);
  const restoresConsumption = showReference && trimmedReference !== '';
  const showCost = isIncrease && !restoresConsumption;
  const typedCost = unitCost.trim();
  const parsedCost = showCost && typedCost !== '' ? parseEuroInput(typedCost) : null;
  const costInvalid = parsedCost !== null && !parsedCost.ok;

  const isLowStock = totalAfter < item.minStockLevel;
  const isOutOfStock = totalAfter === 0;

  const handleSubmit = async () => {
    if (loading || error || saving || !validTarget || invalidQuantity || costInvalid || referenceInvalid) return;
    setSubmitError(false);
    const selectedType = adjustmentTypes.find((a) => a.value === adjustmentType);

    try {
      await onAdjust({
        warehouseId,
        binLocationId: binLocationId || null,
        type: adjustmentType,
        quantity:
          adjustmentType === 'count'
            ? newQuantity
            : adjustmentMode === 'increase'
              ? quantity
              : -quantity,
        reason: selectedType?.label || adjustmentType,
        notes: notes.trim() || undefined,
        expiryDate: expiryDate || (level?.expiryDate ? null : undefined),
        ...(parsedCost?.ok ? { unitCostCents: parsedCost.text } : {}),
        ...(restoresConsumption && !referenceInvalid ? { referenceId: trimmedReference } : {}),
      });
      onClose();
    } catch {
      setSubmitError(true);
    }
  };

  const getStockStatusColor = () => {
    if (isOutOfStock) return 'error.main';
    if (isLowStock) return 'warning.main';
    return 'success.main';
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('stockAdjustment.title')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      isLoading={saving}
      submitDisabled={loading || error || !validTarget || invalidQuantity || costInvalid || referenceInvalid}
      submitColor={adjustmentMode === 'increase' ? 'primary' : 'error'}
      submitLabel={
        adjustmentMode === 'increase'
          ? t('stockAdjustment.increase')
          : t('stockAdjustment.decrease')
      }
      maxWidth="sm"
      fullWidth
    >
      <DialogContent dividers>
        {loading && <LinearProgress aria-label={tc('loading')} sx={{ mb: 2 }} />}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={<Button onClick={onRetry}>{tc('errors.refresh')}</Button>}
          >
            {t('errorLoading')}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('notifications.stockAdjustFailed')}
          </Alert>
        )}
        <TextField
          select
          fullWidth
          label={t('stockLocations.warehouse')}
          value={warehouseId}
          disabled={loading || saving}
          sx={{ mb: 2 }}
          onChange={(e) => {
            setWarehouseId(e.target.value);
            setBinLocationId('');
            markDirty();
          }}
        >
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label={t('stockLocations.bin')}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={binLocationId}
          disabled={loading || saving}
          sx={{ mb: 2 }}
          onChange={(e) => {
            setBinLocationId(e.target.value);
            markDirty();
          }}
        >
          <MenuItem value="">{t('stockLocations.noBin')}</MenuItem>
          {warehouse?.binLocations.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.code}
            </MenuItem>
          ))}
        </TextField>
        {/* Item Info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {item.name}
              </Typography>
              <Chip label={item.partNumber} size="small" sx={{ mt: 0.5 }} />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                {t('stockAdjustment.currentStock')}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {currentQuantity} {t('stockAdjustment.unit')}
              </Typography>
            </Box>
          </Box>
          {item.location && (
            <Typography variant="caption" color="text.secondary">
              {t('stockAdjustment.location', { location: item.location })}
            </Typography>
          )}
        </Paper>

        {/* Adjustment Mode */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t('stockAdjustment.adjustmentMode')}
          </Typography>
          <RadioGroup
            row
            value={adjustmentMode}
            onChange={(e) => {
              setAdjustmentMode(e.target.value as 'increase' | 'decrease');
              markDirty();
            }}
          >
            <FormControlLabel
              value="increase"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AddIcon fontSize="small" color="success" />
                  <span>{t('stockAdjustment.increase')}</span>
                </Box>
              }
            />
            <FormControlLabel
              value="decrease"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RemoveIcon fontSize="small" color="error" />
                  <span>{t('stockAdjustment.decrease')}</span>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        {/* Quantity */}
        <NumberField
          label={t('stockAdjustment.quantityLabel')}
          value={quantity}
          min={1}
          onChange={(val) => {
            setQuantity(val ?? 1);
            markDirty();
          }}
          required
          fullWidth
          error={invalidQuantity}
          helperText={
            invalidQuantity
              ? t('stockLocations.insufficient', { available: currentQuantity - reservedQuantity })
              : undefined
          }
          disabled={loading || error || saving}
          autoFocus
          sx={{ mb: 3 }}
          slotProps={{
            input: {
              endAdornment: t('stockAdjustment.unit'),
            },
          }}
        />

        {/* Adjustment Type/Reason */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="stock-adjustment-reason-label">{t('stockAdjustment.reasonLabel')}</InputLabel>
          <Select
            labelId="stock-adjustment-reason-label"
            value={adjustmentType}
            label={t('stockAdjustment.reasonLabel')}
            onChange={(e) => {
              setAdjustmentType(e.target.value as AdjustmentType);
              markDirty();
            }}
          >
            {adjustmentTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box>
                  <Typography variant="body2">{type.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {type.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Cost of the incoming units (batch 8 T11): optional, never from the catalog */}
        {showReference && (
          <TextField
            label={tCost('adjustment.referenceLabel')}
            helperText={referenceInvalid ? tCost('adjustment.referenceInvalid') : tCost('adjustment.referenceHint')}
            error={referenceInvalid}
            value={referenceId}
            onChange={(e) => {
              setReferenceId(e.target.value);
              markDirty();
            }}
            disabled={loading || error || saving}
            fullWidth
            sx={{ mb: 3 }}
            slotProps={{ htmlInput: { autoComplete: 'off', maxLength: 36 } }}
          />
        )}
        {showCost && (
          <TextField
            label={tCost('adjustment.unitCostLabel')}
            helperText={
              parsedCost && !parsedCost.ok
                ? tCost(`adjustment.unitCostError.${parsedCost.reason}`)
                : tCost('adjustment.unitCostHint')
            }
            error={costInvalid}
            value={unitCost}
            onChange={(e) => {
              setUnitCost(e.target.value);
              markDirty();
            }}
            disabled={loading || error || saving}
            fullWidth
            sx={{ mb: 3 }}
            slotProps={{
              htmlInput: { inputMode: 'decimal', autoComplete: 'off' },
              input: { endAdornment: <Typography color="text.secondary">€</Typography> },
            }}
          />
        )}
        {restoresConsumption && !referenceInvalid && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {tCost('adjustment.restoreNoCost')}
          </Alert>
        )}
        {!isIncrease && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {tCost('adjustment.withdrawalCost')}
          </Alert>
        )}

        {/* Expiry date (#209) */}
        <TextField
          label={t('stockAdjustment.expiryDateLabel')}
          helperText={t('stockAdjustment.expiryDateHint')}
          type="date"
          value={expiryDate}
          onChange={(e) => {
            setExpiryDate(e.target.value);
            markDirty();
          }}
          fullWidth
          sx={{ mb: 3 }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        {/* Notes */}
        <TextField
          label={t('stockAdjustment.notesLabel')}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            markDirty();
          }}
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 3 }}
          placeholder={t('stockAdjustment.notesPlaceholder')}
        />

        <Divider sx={{ my: 2 }} />

        {/* Preview */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t('stockAdjustment.preview')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('stockAdjustment.current')}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {currentQuantity} {t('stockAdjustment.unit')}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="h5"
                color={adjustmentMode === 'increase' ? 'success.main' : 'error.main'}
              >
                {adjustmentMode === 'increase' ? `+${quantity}` : `-${quantity}`}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('stockAdjustment.new')}
              </Typography>
              <Typography variant="h6" fontWeight={600} color={getStockStatusColor()}>
                {newQuantity} {t('stockAdjustment.unit')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Warnings */}
        {isOutOfStock && (
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            {t('stockAdjustment.outOfStockWarning')}
          </Alert>
        )}
        {!isOutOfStock && isLowStock && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            {t('stockAdjustment.lowStockWarning', { min: item.minStockLevel })}
          </Alert>
        )}
        {adjustmentMode === 'increase' &&
          totalAfter >= item.minStockLevel &&
          item.quantity < item.minStockLevel && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              {t('stockAdjustment.aboveMinSuccess')}
            </Alert>
          )}
      </DialogContent>
    </FormDialog>
  );
}
